import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { EyeIcon } from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useToastStore } from '@/stores/toast-store';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { savedViewsKeys } from '@/services/query-keys/saved-views-keys';
import type { SavedView } from '../types';
import type { SavedViewsController } from '../controller';
import type { SavedViewsHooks } from '../hooks';
import { extractNameError } from '../errors';
import { SavedViewDropdown } from './SavedViewDropdown';
import { SavedViewModal } from './SavedViewModal';

interface SavedViewSelectorProps<P> {
  controller: SavedViewsController<P>;
  hooks: SavedViewsHooks<P>;
  currentUserEmail: string | null;
  /**
   * Optional URL builder for the "Copy Link" action. Defaults to
   * `${origin}${pathname}?view_id=<id>` — enough for the common case
   * where the selector sits on the domain's own list page.
   */
  buildShareUrl?: (viewId: number) => string;
  /**
   * Fires whenever the active view id changes as a result of user action
   * inside the selector (pick, create, delete-with-fallback, clear).
   * Scope pages wire this to `navigate({ search: { view_id } })` so the
   * URL stays in sync with the selected view — shareable via copy-paste
   * from the address bar and naturally restored on reload.
   */
  onActiveViewChange?: (viewId: number | null) => void;
}

export function SavedViewSelector<P>({
  controller,
  hooks,
  currentUserEmail: userEmail,
  buildShareUrl,
  onActiveViewChange,
}: SavedViewSelectorProps<P>) {
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();
  // Field-level selectors to avoid re-rendering the selector on every unrelated
  // store change (isApplying toggles, snapshot refreshes during loadView, etc).
  const activeViewId = controller.useStore((s) => s.activeViewId);
  const activeViewName = controller.useStore((s) => s.activeViewName);
  const isDirty = controller.useStore((s) => s.isDirty);
  const setActiveView = controller.useStore((s) => s.setActiveView);
  const setDirty = controller.useStore((s) => s.setDirty);
  const setSavedSnapshot = controller.useStore((s) => s.setSavedSnapshot);
  const reset = controller.useStore((s) => s.reset);

  // SavedViewSelector is a generic component reused across scopes, so it keeps
  // its own useSavedViews subscription rather than accepting the list as a
  // prop. TanStack Query deduplicates this with any parallel call on the host
  // page (e.g. InventoryPage), so the extra subscription is free at runtime.
  const { data: viewsData } = hooks.useSavedViews();
  const createView = hooks.useCreateSavedView();
  const updateView = hooks.useUpdateSavedView();
  const deleteView = hooks.useDeleteSavedView();
  const setDefault = hooks.useSetDefaultView();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingView, setEditingView] = useState<SavedView | null>(null);
  const [modalNameError, setModalNameError] = useState<string | null>(null);
  // Covers the window between `mutateAsync` resolving and `closeModal` firing,
  // when the mutation's onSuccess has already inserted the new view into the
  // cached list but the modal is still open. Without this, SavedViewModal's
  // live name-clash check would flash "You already have a view with this
  // name." for one render. The modal keys its error suppression off
  // `isLoading`, so we merge this flag into that prop.
  const [isModalSaving, setIsModalSaving] = useState(false);
  // Override for the modal's initialIsPublic — used by the "Make Public"
  // conflict flow to pre-toggle "Share with team" even when the source view
  // is still private. `null` means "use editingView?.is_public as before".
  const [modalInitialIsPublic, setModalInitialIsPublic] = useState<boolean | null>(null);
  const [deleteConfirmView, setDeleteConfirmView] = useState<SavedView | null>(null);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalNameError(null);
    setModalInitialIsPublic(null);
  }, []);

  // Stable reference so SavedViewDropdown's memoized rows keep their callback
  // identity (onClose feeds into handleRowSelect — an inline arrow would make
  // the memo comparison miss every render).
  const closeDropdown = useCallback(() => setDropdownOpen(false), []);
  // Captured at open time so the confirm dialog title doesn't flash "undefined"
  // during the close animation after deleteConfirmView is cleared.
  const lastDeleteNameRef = useRef<string>('');
  const selectorRef = useRef<HTMLButtonElement>(null);
  // Monotonic counter — late getDetail responses compare against this and bail
  // when a newer selection has started, preventing stale payloads from winning.
  const selectRequestIdRef = useRef(0);

  // Memoize so downstream useMemo deps (in the modal) don't invalidate every
  // render just because `?? []` minted a new empty array.
  const views = useMemo<SavedView[]>(() => viewsData?.results ?? [], [viewsData?.results]);
  const defaultViewId = viewsData?.default_view_id ?? null;

  const activeView = views.find((v) => v.id === activeViewId);
  const canSave = activeView && activeView.author_email === userEmail && !activeView.is_system;

  // Dirty-state detection via a single adapter subscription. The adapter
  // owns the domain stores; we just recompute on any notification. Cheap —
  // diff uses lodash.isequal over a small payload.
  useEffect(() => {
    const unsub = controller.adapter.subscribeToChanges(() => {
      setDirty(controller.computeIsDirty());
    });
    return unsub;
  }, [controller, setDirty]);

  const handleSelectView = useCallback(
    async (view: SavedView) => {
      const requestId = ++selectRequestIdRef.current;
      try {
        const detail = await hooks.service.getDetail(view.id);
        if (requestId !== selectRequestIdRef.current) return;
        controller.loadView(view.id, view.name, detail.payload);
        onActiveViewChange?.(view.id);
      } catch (err) {
        if (requestId !== selectRequestIdRef.current) return;
        // 404 → view was deleted by someone else (or private→public toggle).
        // Refetch the list so the stale row disappears instead of staying
        // clickable with a phantom id.
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          addToast('error', 'View no longer exists');
          queryClient.invalidateQueries({ queryKey: savedViewsKeys.list(hooks.scope) });
        } else {
          addToast('error', 'Failed to load view');
        }
      }
    },
    [addToast, controller, hooks.scope, hooks.service, onActiveViewChange, queryClient],
  );

  const handleSave = useCallback(async () => {
    if (!activeViewId || !canSave) return;
    try {
      const payload = controller.capture();
      await updateView.mutateAsync({ id: activeViewId, data: { payload } });
      setSavedSnapshot(payload);
      addToast('success', 'View saved');
    } catch {
      addToast('error', 'Failed to save view');
    }
  }, [activeViewId, canSave, updateView, setSavedSnapshot, addToast, controller]);

  const handleSaveAsNew = useCallback(() => {
    setModalMode('create');
    setEditingView(null);
    setModalNameError(null);
    setModalInitialIsPublic(null);
    setModalOpen(true);
  }, []);

  const handleModalSave = useCallback(
    async (data: { name: string; description: string; is_public: boolean }) => {
      setIsModalSaving(true);
      try {
        if (modalMode === 'create') {
          const payload = controller.capture();
          const created = await createView.mutateAsync({
            name: data.name,
            description: data.description || undefined,
            is_public: data.is_public,
            payload,
          });
          setActiveView(created.id, created.name, controller.capture());
          onActiveViewChange?.(created.id);
          addToast('success', `View "${data.name}" created`);
        } else if (modalMode === 'edit' && editingView) {
          await updateView.mutateAsync({
            id: editingView.id,
            data: {
              name: data.name,
              description: data.description || undefined,
              is_public: data.is_public,
            },
          });
          if (editingView.id === activeViewId) {
            setActiveView(
              editingView.id,
              data.name,
              controller.useStore.getState().savedSnapshot,
            );
          }
          addToast('success', `View "${data.name}" updated`);
        }
        closeModal();
      } catch (err) {
        // Surface backend field-level name errors inside the modal; keep
        // generic toast for anything else (network, 5xx, permission).
        const nameErr = extractNameError(err);
        if (nameErr) {
          setModalNameError(nameErr);
          return;
        }
        addToast(
          'error',
          modalMode === 'create' ? 'Failed to create view' : 'Failed to update view',
        );
      } finally {
        setIsModalSaving(false);
      }
    },
    [modalMode, editingView, createView, updateView, activeViewId, setActiveView, addToast, controller, onActiveViewChange, closeModal],
  );

  const openEditModal = useCallback(
    (view: SavedView, opts?: { initialIsPublic?: boolean; nameError?: string }) => {
      setModalMode('edit');
      setEditingView(view);
      setModalInitialIsPublic(opts?.initialIsPublic ?? null);
      setModalNameError(opts?.nameError ?? null);
      setModalOpen(true);
    },
    [],
  );

  const handleEditView = useCallback((view: SavedView) => openEditModal(view), [openEditModal]);

  const handleDeleteView = useCallback(async (view: SavedView) => {
    lastDeleteNameRef.current = view.name;
    setDeleteConfirmView(view);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteConfirmView) return;
    const view = deleteConfirmView;
    try {
      await deleteView.mutateAsync(view.id);
      if (activeViewId === view.id) {
        const systemView = views.find((v) => v.is_system);
        if (systemView) {
          try {
            const detail = await hooks.service.getDetail(systemView.id);
            controller.loadView(systemView.id, systemView.name, detail.payload);
            onActiveViewChange?.(systemView.id);
          } catch {
            // Payload fetch failed — committing a phantom snapshot would
            // desync dirty detection, so clear active state and let the
            // user pick explicitly. Error surfaced as a toast.
            reset();
            onActiveViewChange?.(null);
            addToast('error', 'Failed to load fallback view — please pick one manually');
          }
        } else {
          // No system view available — clear active state so UI doesn't hold
          // a dangling reference to the deleted view.
          reset();
          onActiveViewChange?.(null);
        }
      }
      addToast('success', `View "${view.name}" deleted`);
    } catch {
      addToast('error', 'Failed to delete view');
    }
    setDeleteConfirmView(null);
  }, [deleteConfirmView, deleteView, activeViewId, views, reset, addToast, controller, hooks.service, onActiveViewChange]);

  const handleSetDefault = useCallback(
    async (viewId: number | null) => {
      try {
        await setDefault.mutateAsync(viewId);
        addToast('success', viewId ? 'Default view set' : 'Default view cleared');
      } catch {
        addToast('error', 'Failed to update default view');
      }
    },
    [setDefault, addToast],
  );

  const handleTogglePublic = useCallback(
    async (view: SavedView) => {
      // Public→private never conflicts. For private→public, check the cache
      // first and short-circuit into the rename modal if another public view
      // already owns this name.
      if (!view.is_public) {
        const publicClash = views.some(
          (v) => v.id !== view.id && v.is_public && v.name === view.name,
        );
        if (publicClash) {
          openEditModal(view, {
            initialIsPublic: true,
            nameError: 'A public view with this name already exists.',
          });
          return;
        }
      }
      try {
        await updateView.mutateAsync({
          id: view.id,
          data: { is_public: !view.is_public },
        });
        addToast('success', view.is_public ? 'View is now private' : 'View is now public');
      } catch (err) {
        // Cache-race fallback: server rejected the flip on a collision we
        // didn't see. Same UX as the pre-check.
        const nameErr = extractNameError(err);
        if (nameErr && !view.is_public) {
          openEditModal(view, { initialIsPublic: true, nameError: nameErr });
          return;
        }
        addToast('error', 'Failed to update view');
      }
    },
    [updateView, addToast, views, openEditModal],
  );

  const handleCopyLink = useCallback(
    async (view: SavedView) => {
      let url: string;
      if (buildShareUrl) {
        url = buildShareUrl(view.id);
      } else {
        // Preserve the current search params and only override view_id —
        // a naive `?view_id=N` would strip unrelated context (e.g. tab).
        const params = new URLSearchParams(window.location.search);
        params.set('view_id', String(view.id));
        url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
      }
      try {
        await navigator.clipboard.writeText(url);
        addToast('success', 'Link copied to clipboard');
      } catch {
        addToast('error', 'Failed to copy link');
      }
    },
    [addToast, buildShareUrl],
  );

  return (
    <div className="flex flex-col gap-2">
      {/* Full-width dropdown trigger */}
      <button
        ref={selectorRef}
        onClick={() => {
          const next = !dropdownOpen;
          setDropdownOpen(next);
          if (next) {
            queryClient.invalidateQueries({ queryKey: savedViewsKeys.list(hooks.scope) });
          }
        }}
        className={clsx(
          'flex w-full items-center gap-2 px-3 py-2 rounded-md text-sm font-medium',
          'bg-white text-gray-800 border border-gray-300 hover:border-gray-400',
          'focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500',
          'transition-colors',
        )}
      >
        <EyeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <span className="flex-1 truncate text-left flex items-center gap-1.5 min-w-0">
          <span className="truncate">{activeViewName}</span>
          {isDirty && (
            <span
              className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0"
              role="img"
              title="Unsaved changes"
              aria-label="Unsaved changes"
            />
          )}
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
      </button>

      {/* Save actions — Save is primary; secondary is outlined when paired, primary when alone */}
      {isDirty && (
        <div className="flex items-center justify-center gap-2">
          {canSave && (
            <button
              type="button"
              onClick={handleSave}
              disabled={updateView.isPending}
              className="inline-flex items-center justify-center h-6 px-3 rounded text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Save
            </button>
          )}
          <button
            type="button"
            onClick={handleSaveAsNew}
            className={clsx(
              'inline-flex items-center justify-center h-6 px-3 rounded text-xs font-semibold transition-colors whitespace-nowrap',
              canSave
                ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                : 'bg-blue-600 text-white hover:bg-blue-700',
            )}
          >
            {canSave ? 'Save as copy' : 'Save as new'}
          </button>
        </div>
      )}

      <SavedViewDropdown
        isOpen={dropdownOpen}
        onClose={closeDropdown}
        anchorRef={selectorRef}
        views={views}
        defaultViewId={defaultViewId}
        activeViewId={activeViewId}
        currentUserEmail={userEmail}
        onSelectView={handleSelectView}
        onSetDefault={handleSetDefault}
        onTogglePublic={handleTogglePublic}
        onEditView={handleEditView}
        onDeleteView={handleDeleteView}
        onCopyLink={handleCopyLink}
      />

      <SavedViewModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSave={handleModalSave}
        isLoading={createView.isPending || updateView.isPending || isModalSaving}
        title={modalMode === 'create' ? 'Save View' : 'Edit View'}
        initialName={modalMode === 'edit' ? editingView?.name : ''}
        initialDescription={modalMode === 'edit' ? editingView?.description ?? '' : ''}
        initialIsPublic={
          modalInitialIsPublic ?? (modalMode === 'edit' ? editingView?.is_public ?? false : false)
        }
        views={views}
        currentUserEmail={userEmail}
        currentViewId={modalMode === 'edit' ? editingView?.id : undefined}
        externalNameError={modalNameError}
      />

      <ConfirmDialog
        show={!!deleteConfirmView}
        onClose={() => setDeleteConfirmView(null)}
        onConfirm={confirmDelete}
        title={`Delete "${deleteConfirmView?.name ?? lastDeleteNameRef.current}"?`}
        titleColor="default"
        confirmLabel="Delete"
        confirmColor="red"
        isLoading={deleteView.isPending}
      >
        <p className="text-sm text-gray-600">
          This action cannot be undone. Users who have this view set as default will be reset.
        </p>
      </ConfirmDialog>
    </div>
  );
}
